import sparkMD5 from 'spark-md5';
import { getConfigAssets } from '@/i18n/assets';
import { formatFontFamilyName } from '@/utils/text-kit';

/**
 * 规范化子集字符集：去重、排序。
 *
 * @param chars - 字符集
 * @returns 规范化后的字符集
 */
export const normalizeChars = (chars: string) => [...new Set(chars)].sort().join('');

/**
 * 构造请求记账维度的键：按"请求拥有者 + 字体"区分。
 *
 * 避免同一字体下不同请求拥有者的请求互相 abort。
 *
 * 用 `\u0000` 连接两段，而非直接拼接或可见分隔符（`-`、`::` 等）：
 * - 直接拼接会在边界歧义时碰撞（如 `owner1`+`23` 与 `owner12`+`3` 同为 `owner123`）
 * - 请求拥有者 ID、字体名中几乎不会出现 NUL，比常见可见字符更安全
 *
 * @param ownerId - 请求拥有者 ID
 * @param fontFamily - 字体族名称
 * @returns 请求单元键
 */
export const createOwnerRequestKey = (ownerId: string, fontFamily: string) => `${ownerId}\u0000${fontFamily}`;

/**
 * 创建子集名称
 * @param fontFamily - 字体名称
 * @param chars - 字符集
 * @returns 子集名称
 */
export const createSubsetName = (fontFamily: string, chars: string) => {
    return `${formatFontFamilyName(fontFamily)}-${sparkMD5.hash(chars)}`;
};

/**
 * 判断字符集是否被覆盖
 * @param coveredChars - 覆盖的字符集
 * @param requestedChars - 请求的字符集
 * @returns 是否被覆盖
 */
export const isCharSetCovered = (coveredChars: string, requestedChars: string) => {
    const covered = new Set(normalizeChars(coveredChars));
    return [...normalizeChars(requestedChars)].every((char) => covered.has(char));
};

/**
 * 构造字体工具服务的子集请求地址。
 *
 * @param source - 原始字体文件地址
 * @param chars - 需要包含的字符
 * @param type - 返回的字体格式
 * @returns 字体子集请求地址
 */
export const createFontSubsetURL = (source: string, chars: string, type: 'ttf' | 'woff2') => {
    const searchParams = new URLSearchParams({ src: source, type, chars });
    return `${getConfigAssets().fontTool}?${searchParams}`;
};

/**
 * 调用字体工具服务拉取指定格式的字体子集，并限制最长等待时间。
 * @param source - 字体文件地址
 * @param chars - 字符集
 * @param type - 子集格式
 * @param controller - 请求控制器
 * @returns 字体子集字节，失败时抛出错误
 */
export const fetchSubsetBytes = async (
    source: string,
    chars: string,
    type: 'ttf' | 'woff2',
    controller: AbortController,
) => {
    const timeoutMs = 30000;
    const timer = globalThis.setTimeout(() => controller.abort(), timeoutMs);
    try {
        const response = await fetch(createFontSubsetURL(source, chars, type), { signal: controller.signal });
        if (!response.ok) throw new Error(`Failed to fetch font subset: ${response.statusText}`);
        return new Uint8Array(await response.arrayBuffer());
    } finally {
        globalThis.clearTimeout(timer);
    }
};
